import { defineMessages, type IntlShape, type MessageDescriptor } from 'react-intl';

import type { Artifact } from './artifacts/state';

const OPEN_LABEL_MESSAGES = defineMessages({
  openNote: {
    defaultMessage: 'Open note',
    id: 'zuPVEX83/V',
    description: 'Button label: open the note artifact panel',
  },
  openSession: {
    defaultMessage: 'Open session',
    id: 'Oe21yOoucN',
    description: 'Button label: open the session artifact panel',
  },
  openPatient: {
    defaultMessage: 'Open patient',
    id: 'eAwkRy98nP',
    description: 'Button label: open the patient artifact panel',
  },
  openLetter: {
    defaultMessage: 'Open letter',
    id: 'kqIUeP6vpX',
    description: 'Button label: open the letter artifact panel',
  },
  openTasks: {
    defaultMessage: 'Open tasks',
    id: 'dJq6Mmbyca',
    description: 'Button label: open the tasks artifact panel',
  },
  openCodes: {
    defaultMessage: 'Open codes',
    id: 'unpVMN/2az',
    description: 'Button label: open the clinical codes artifact panel',
  },
  openDocument: {
    defaultMessage: 'Open document',
    id: 'mO5Xf/N9JY',
    description: 'Button label: open the document artifact panel',
  },
  viewAll: {
    defaultMessage: 'View all',
    id: 'ye9170w2ew',
    description: 'Button label: view all objects created in chat',
  },
  editTemplate: {
    defaultMessage: 'Edit template',
    id: 'LePYdbs5Yj',
    description: 'Button label: open the template builder',
  },
  editForm: {
    defaultMessage: 'Edit form',
    id: 'orMlSIY8AR',
    description: 'Button label: open the form template editor',
  },
  previewLibraryDocument: {
    defaultMessage: 'Preview',
    id: 'cqQ8BSSsfW',
    description: 'Button label: preview a library document',
  },
  openPresentation: {
    defaultMessage: 'Open presentation',
    id: 'iyMf/BOMi+',
    description: 'Button label: open the presentation artifact panel',
  },
  openMemory: {
    defaultMessage: 'Open memory',
    id: '/8xFXKvzNM',
    description: 'Button label: open the agent memory artifact panel',
  },
  openRoutine: {
    defaultMessage: 'Open routine',
    id: '9ZZZQG3os3',
    description: 'Button label: open the routine artifact panel',
  },
  previewPdfExport: {
    defaultMessage: 'Preview PDF',
    id: 'MsD/4nxM1z',
    description: 'Button label: preview an exported PDF document',
  },
  previewExport: {
    defaultMessage: 'Preview',
    id: 'RwUbn4qPzg',
    description: 'Button label: preview an exported document',
  },
  previewRunAttachment: {
    defaultMessage: 'Preview',
    id: 'TKadBfDNDq',
    description: 'Button label: preview a run attachment',
  },
  openSessions: {
    defaultMessage: 'Open sessions',
    id: 'ly6chea/BO',
    description: 'Button label: open the session list artifact panel',
  },
  openCalendar: {
    defaultMessage: 'Open calendar',
    id: '2kicsn3a0Y',
    description: 'Button label: open the calendar artifact panel',
  },
  openMail: {
    defaultMessage: 'Open mail',
    id: 'dhtNygaSC/',
    description: 'Button label: open the mail artifact panel',
  },
  openFiles: {
    defaultMessage: 'Open files',
    id: 'Ba6evpj2jq',
    description: 'Button label: open the files artifact panel',
  },
  openEligibility: {
    defaultMessage: 'Open eligibility',
    id: 'aesqXWZwwc',
    description: 'Button label: open the insurance eligibility artifact panel',
  },
  openInsurance: {
    defaultMessage: 'Open insurance',
    id: '3y2Y4aww3z',
    description: 'Button label: open the insurance artifact panel',
  },
  verifyMapping: {
    defaultMessage: 'Verify mapping',
    id: 'kbthDpa+bj',
    description: 'Button label: open the note-push mapping verification panel',
  },
  openArtifacts: {
    defaultMessage: 'Open artifacts',
    id: '5Uxs7Svzku',
    description: 'Button label: open the Work home Artifacts panel',
  },
  open: {
    defaultMessage: 'Open',
    id: 'gl8gFC0MYn',
    description: 'Button label: open an artifact panel (generic fallback)',
  },
});

// Total over every kind without a variant branch, so adding an artifact type
// without a label is a compile error rather than a fallthrough.
const KIND_MESSAGES: Record<
  Exclude<Artifact['type'], 'session' | 'document-export' | 'insurance'>,
  MessageDescriptor
> = {
  patient: OPEN_LABEL_MESSAGES.openPatient,
  note: OPEN_LABEL_MESSAGES.openNote,
  letter: OPEN_LABEL_MESSAGES.openLetter,
  tasks: OPEN_LABEL_MESSAGES.openTasks,
  codes: OPEN_LABEL_MESSAGES.openCodes,
  document: OPEN_LABEL_MESSAGES.openDocument,
  'created-objects': OPEN_LABEL_MESSAGES.viewAll,
  'template-builder': OPEN_LABEL_MESSAGES.editTemplate,
  'form-template': OPEN_LABEL_MESSAGES.editForm,
  'library-document': OPEN_LABEL_MESSAGES.previewLibraryDocument,
  presentation: OPEN_LABEL_MESSAGES.openPresentation,
  memory: OPEN_LABEL_MESSAGES.openMemory,
  workflow: OPEN_LABEL_MESSAGES.openRoutine,
  'run-attachment': OPEN_LABEL_MESSAGES.previewRunAttachment,
  'session-list': OPEN_LABEL_MESSAGES.openSessions,
  calendar: OPEN_LABEL_MESSAGES.openCalendar,
  mail: OPEN_LABEL_MESSAGES.openMail,
  files: OPEN_LABEL_MESSAGES.openFiles,
  'note-push-verification': OPEN_LABEL_MESSAGES.verifyMapping,
  library: OPEN_LABEL_MESSAGES.openArtifacts,
};

/**
 * Map artifact kind to the "Open" button label. Short verbs keep the button
 * narrow; the panel header carries the full title.
 */
export const artifactOpenLabel = (intl: IntlShape, artifact: Artifact): string => {
  if (artifact.type === 'session') {
    return intl.formatMessage(
      artifact.focusDocumentId
        ? OPEN_LABEL_MESSAGES.openNote
        : OPEN_LABEL_MESSAGES.openSession
    );
  }
  if (artifact.type === 'document-export') {
    return intl.formatMessage(
      artifact.forcePdfExtension
        ? OPEN_LABEL_MESSAGES.previewPdfExport
        : OPEN_LABEL_MESSAGES.previewExport
    );
  }
  if (artifact.type === 'insurance') {
    return intl.formatMessage(
      artifact.variant === 'eligibility'
        ? OPEN_LABEL_MESSAGES.openEligibility
        : OPEN_LABEL_MESSAGES.openInsurance
    );
  }
  // Object.hasOwn: out-of-type values (the reason the fallback exists) must
  // not resolve Object.prototype members ('constructor', …) as descriptors.
  return intl.formatMessage(
    Object.hasOwn(KIND_MESSAGES, artifact.type)
      ? KIND_MESSAGES[artifact.type]
      : OPEN_LABEL_MESSAGES.open
  );
};
